import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Divider,
  Stack,
  Drawer,
  ListItemButton,
  Collapse,
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Dashboard,
  People,
  Group,
  Schedule,
  Note,
  Receipt,
  AccountCircle,
  Security,
  AccessTime,
  Assignment,
  Phone,
  Email,
  Home,
  Warning,
  CheckCircle,
  Info,
  Lock,
  Visibility,
  Settings,
  Help,
  Timeline,
  Psychology,
  FileUpload,
  NotificationImportant,
  School,
  Build,
  VerifiedUser,
  Speed,
  Troubleshoot,
  QuestionMark,
  Menu as MenuIcon,
  Close as CloseIcon,
  KeyboardArrowUp,
  Toc,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const DocumentationPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("lg")); // For drawer behavior
  const [drawerOpen, setDrawerOpen] = useState(!isSmallScreen);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  // Per-page SEO: branded title + meta description for the public
  // documentation page so it indexes/unfurls under its own context.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Documentation | Felice Care Management System";

    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content") ?? null;
    desc?.setAttribute(
      "content",
      "User documentation for the Felice Care Management System — staff guide, roster, billing, NDIS invoicing and care notes for Felice Care and Disability Services."
    );

    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) desc?.setAttribute("content", prevDesc);
    };
  }, []);

  // Responsive typography styles
  const responsiveH4Style = {
    fontSize: {
      xs: "1.5rem", // 24px on extra small screens
      sm: "1.75rem", // 28px on small screens
      md: "2rem", // 32px on medium screens
      lg: "2.125rem", // 34px on large screens (h4 default)
    },
    textAlign: { xs: "center", sm: "left" } as const,
    wordBreak: "break-word" as const,
  };

  // Handle responsive drawer behavior
  useEffect(() => {
    setDrawerOpen(!isSmallScreen);
  }, [isSmallScreen]);

  const tableOfContents = [
    {
      id: "overview",
      title: "System Overview",
      icon: <Info />,
      subsections: [],
    },
    {
      id: "getting-started",
      title: "Getting Started",
      icon: <School />,
      subsections: [
        { id: "login", title: "Login to Your Account" },
        { id: "dashboard", title: "Explore Your Dashboard" },
        { id: "navigation", title: "Navigate Using the Sidebar" },
        { id: "profile", title: "Update Your Profile" },
        { id: "core-features", title: "Start Using Core Features" },
      ],
    },
    {
      id: "user-roles",
      title: "User Roles & Permissions",
      icon: <Security />,
      subsections: [
        { id: "administrator", title: "Administrator" },
        { id: "manager", title: "Manager" },
        { id: "care-coordinator", title: "Care Coordinator" },
        { id: "staff-carer", title: "Staff/Carer" },
      ],
    },
    {
      id: "system-modules",
      title: "System Modules",
      icon: <Dashboard />,
      subsections: [
        { id: "dashboard-module", title: "Dashboard" },
        { id: "client-management", title: "Client Management" },
        { id: "staff-management", title: "Staff Management" },
        { id: "roster-scheduling", title: "Roster & Scheduling" },
        { id: "care-notes", title: "Care Notes" },
        { id: "billing-payroll", title: "Billing & Payroll" },
      ],
    },
    {
      id: "workflows",
      title: "Common Workflows",
      icon: <Timeline />,
      subsections: [
        { id: "shift-management", title: "Shift Management Workflow" },
        { id: "client-onboarding", title: "Client Onboarding Process" },
        { id: "care-documentation", title: "Care Documentation Flow" },
        { id: "billing-process", title: "Billing and Payroll Process" },
      ],
    },
    {
      id: "best-practices",
      title: "Best Practices & Guidelines",
      icon: <CheckCircle />,
      subsections: [
        { id: "data-security", title: "Data Security" },
        { id: "care-doc-practices", title: "Care Documentation" },
        { id: "schedule-management", title: "Schedule Management" },
        { id: "communication", title: "Communication" },
      ],
    },
    {
      id: "security-guidelines",
      title: "Security Guidelines",
      icon: <Lock />,
      subsections: [
        { id: "password-security", title: "Password Security" },
        { id: "access-control", title: "Access Control" },
        { id: "data-protection", title: "Data Protection" },
      ],
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting Guide",
      icon: <Troubleshoot />,
      subsections: [
        { id: "cannot-login", title: "Cannot Log In" },
        { id: "page-loading", title: "Page Loading Slowly" },
        { id: "cannot-save", title: "Cannot Save Changes" },
        { id: "missing-schedule", title: "Missing Schedule Information" },
      ],
    },
    {
      id: "advanced-features",
      title: "Advanced Features",
      icon: <Settings />,
      subsections: [
        { id: "reporting-analytics", title: "Reporting & Analytics" },
        { id: "system-config", title: "System Configuration" },
        { id: "data-import-export", title: "Data Import/Export" },
        { id: "automated-alerts", title: "Automated Alerts" },
      ],
    },
    {
      id: "training-resources",
      title: "Training & Learning Resources",
      icon: <Help />,
      subsections: [
        { id: "new-user-training", title: "New User Training" },
        { id: "advanced-training", title: "Advanced Training" },
        { id: "ongoing-support", title: "Ongoing Support" },
      ],
    },
    {
      id: "support",
      title: "Support & Contact Information",
      icon: <Phone />,
      subsections: [
        { id: "support-contacts", title: "Support Contacts" },
        { id: "emergency-support", title: "Emergency Support" },
      ],
    },
    {
      id: "faq",
      title: "Frequently Asked Questions",
      icon: <QuestionMark />,
      subsections: [],
    },
  ];

  const scrollToSection = (sectionId: string) => {
    console.log("=== NAVIGATION CLICK DEBUG ===");
    console.log("Section ID:", sectionId);
    console.log("isSmallScreen:", isSmallScreen);
    console.log("drawerOpen:", drawerOpen);
    console.log("Current URL hash:", window.location.hash);

    // Close drawer on smaller screens FIRST
    if (isSmallScreen && drawerOpen) {
      console.log("Closing drawer for small screen...");
      setDrawerOpen(false);

      // Wait for drawer to close before scrolling
      setTimeout(() => {
        console.log("Drawer closed, attempting scroll...");
        performScroll(sectionId);
      }, 350); // Wait for drawer animation
    } else {
      console.log("Scrolling immediately (large screen or drawer closed)...");
      performScroll(sectionId);
    }
  };

  const performScroll = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    console.log("Target element:", element);

    if (element) {
      console.log("Element position:", element.getBoundingClientRect());

      // Try multiple scroll methods
      try {
        // Method 1: scrollIntoView
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
        console.log("scrollIntoView called successfully");

        // Method 2: Alternative scroll (fallback)
        setTimeout(() => {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset + rect.top - 70; // Account for any fixed headers
          window.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          });
          console.log("Alternative scroll method called");
        }, 100);
      } catch (error) {
        console.error("Scroll error:", error);
        // Fallback: instant scroll
        element.scrollIntoView();
      }
    } else {
      console.error("Element not found with ID:", sectionId);
      // Debug: list all available IDs
      const allIds = Array.from(document.querySelectorAll("[id]")).map(
        (el) => el.id,
      );
      console.log(
        "Available IDs:",
        allIds.filter((id) => id.includes(sectionId.split("-")[0])),
      );
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const NavigationDrawer = () => (
    <Box
      sx={{
        width: 300,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        // Ensure the drawer content can receive clicks
        pointerEvents: "auto",
        position: "relative",
        zIndex: 1,
      }}
      onClick={(e) => {
        console.log("NavigationDrawer clicked:", e.target);
        // Don't stop propagation - let clicks through to children
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Toc color="primary" sx={{ mr: 1 }} />
          <Typography variant="h6" color="primary">
            Table of Contents
          </Typography>
          {isSmallScreen && (
            <Button
              size="small"
              onClick={() => setDrawerOpen(false)}
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </Button>
          )}
        </Box>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate("/login")}
          startIcon={<Home />}
          sx={{
            mb: 1,
            background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            boxShadow: "0 3px 5px 2px rgba(25, 118, 210, .3)",
            color: "white",
            fontWeight: "bold",
            fontSize: "1rem",
            py: 1.5,
            borderRadius: 2,
            textTransform: "none",
            transition: "all 0.3s ease-in-out",
            border: "2px solid transparent",
            "&:hover": {
              background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
              boxShadow: "0 6px 10px 2px rgba(25, 118, 210, .4)",
              transform: "translateY(-2px)",
              border: "2px solid #fff",
            },
            "&:active": {
              transform: "translateY(0px)",
              boxShadow: "0 2px 4px 1px rgba(25, 118, 210, .2)",
            },
          }}
        >
          Back to Login
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <List dense>
          {tableOfContents.map((section) => (
            <React.Fragment key={section.id}>
              <ListItemButton
                onClick={(e) => {
                  console.log("=== MAIN SECTION CLICK ===");
                  console.log("Event target:", e.target);
                  console.log("Current target:", e.currentTarget);
                  console.log("Section ID:", section.id);
                  console.log(
                    "Screen type - isSmallScreen:",
                    isSmallScreen,
                    "isMobile:",
                    isMobile,
                  );

                  // Ensure click is processed
                  e.preventDefault();
                  e.stopPropagation();

                  scrollToSection(section.id);
                  if (section.subsections.length > 0) {
                    toggleSection(section.id);
                  }
                }}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  cursor: "pointer",
                  userSelect: "none",
                  minHeight: 48, // Larger touch target
                  position: "relative",
                  zIndex: 2,
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                  "&:active": {
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  },
                  // Better touch handling
                  "@media (hover: none)": {
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {section.icon}
                </ListItemIcon>
                <ListItemText
                  primary={section.title}
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: section.subsections.length > 0 ? 600 : 400,
                  }}
                />
                {section.subsections.length > 0 &&
                  (expandedSections[section.id] ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  ))}
              </ListItemButton>

              {section.subsections.length > 0 && (
                <Collapse
                  in={expandedSections[section.id]}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding dense>
                    {section.subsections.map((subsection) => (
                      <ListItemButton
                        key={subsection.id}
                        onClick={(e) => {
                          console.log("=== SUBSECTION CLICK ===");
                          console.log("Event target:", e.target);
                          console.log("Subsection ID:", subsection.id);
                          console.log(
                            "Screen type - isSmallScreen:",
                            isSmallScreen,
                          );

                          // Ensure click is processed
                          e.preventDefault();
                          e.stopPropagation();

                          scrollToSection(subsection.id);
                        }}
                        sx={{
                          pl: 6,
                          py: 1,
                          borderRadius: 1,
                          cursor: "pointer",
                          userSelect: "none",
                          minHeight: 40, // Larger touch target
                          position: "relative",
                          zIndex: 2,
                          "&:hover": {
                            bgcolor: "secondary.light",
                            color: "secondary.contrastText",
                          },
                          "&:active": {
                            bgcolor: "secondary.main",
                            color: "secondary.contrastText",
                          },
                          // Better touch handling
                          "@media (hover: none)": {
                            "&:hover": {
                              bgcolor: "transparent",
                            },
                          },
                        }}
                      >
                        <ListItemText
                          primary={subsection.title}
                          primaryTypographyProps={{
                            variant: "caption",
                            color: "text.secondary",
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );

  const userRoles = [
    {
      name: "Administrator",
      level: 4,
      color: "error",
      description:
        "Full system access - can manage all users, settings, and data",
      permissions: [
        "Create and manage user accounts",
        "Access all system modules",
        "Configure system settings",
        "View all reports and analytics",
        "Manage staff schedules and assignments",
        "Handle billing and payroll operations",
      ],
    },
    {
      name: "Manager",
      level: 3,
      color: "warning",
      description: "Management access - can oversee operations and staff",
      permissions: [
        "Manage staff profiles and schedules",
        "Access billing and payroll",
        "View reports and analytics",
        "Manage client information",
        "Approve time-off requests",
        "Assign shifts and duties",
      ],
    },
    {
      name: "Care Coordinator",
      level: 2,
      color: "info",
      description: "Coordination access - can manage schedules and client care",
      permissions: [
        "View and manage staff schedules",
        "Access client information",
        "Create and update care notes",
        "View shift assignments",
        "Coordinate care plans",
        "Access basic reports",
      ],
    },
    {
      name: "Staff/Carer",
      level: 1,
      color: "success",
      description:
        "Basic access - can view schedules and update care information",
      permissions: [
        "View personal schedule and shifts",
        "Clock in and out of shifts",
        "Update care notes for assigned clients",
        "View client basic information",
        "Request time off",
        "Access personal profile",
      ],
    },
  ];

  const modules = [
    {
      name: "Dashboard",
      icon: <Dashboard />,
      description:
        "Real-time overview of system status, recent activities, and key metrics across all microservices",
      features: [
        "Microservices health monitoring",
        "Real-time activity feed with event notifications",
        "Quick access to common tasks and workflows",
        "Role-based information display with service status",
        "System performance metrics and alerts",
      ],
    },
    {
      name: "Client Management",
      icon: <People />,
      description:
        "Comprehensive client management powered by dedicated Client Service microservice",
      features: [
        "Complete client profile management with medical history",
        "Advanced care plan creation and tracking",
        "Service history with audit trails",
        "Emergency contact management",
        "Document storage and retrieval system",
        "Integration with billing for automated invoicing",
      ],
    },
    {
      name: "Staff Management",
      icon: <Group />,
      description:
        "Advanced staff management through dedicated Staff Service with availability tracking",
      features: [
        "Comprehensive staff profile management",
        "Role-based permission system with JWT integration",
        "Employment details and qualification tracking",
        "Smart availability scheduling system",
        "Leave request management with approval workflows",
        "Performance metrics and document management",
      ],
    },
    {
      name: "Roster & Scheduling",
      icon: <Schedule />,
      description:
        "Advanced shift management with dual completion workflow and smart assignment optimization",
      features: [
        "Smart shift creation with optimization algorithms",
        "Advanced completion workflow (staff → admin approval)",
        "Real-time availability tracking and conflict detection",
        "Precise time clock with overtime calculation",
        "Shift swap coordination system",
        "Calendar views with status progression tracking",
        "Integration with billing for automated payroll",
      ],
    },
    {
      name: "Care Notes",
      icon: <Note />,
      description:
        "Comprehensive care documentation system with real-time updates and audit trails",
      features: [
        "Real-time care note creation with timestamps",
        "Client-specific documentation with care plan integration",
        "Progress tracking with milestone notifications",
        "Incident reporting with automated alerts",
        "Complete audit trail with user tracking",
        "Template-based notes for consistency",
      ],
    },
    {
      name: "Billing & Payroll",
      icon: <Receipt />,
      description:
        "Comprehensive automated billing system with PDF generation and multi-payment processing",
      features: [
        "Automated invoice generation from completed shifts",
        "Professional PDF invoice and receipt generation",
        "Multi-method payment processing (Credit, Bank, Cash, Check)",
        "Real-time payment status tracking and notifications",
        "Automated payroll calculation from shift data",
        "Advanced financial reporting and analytics",
        "Integration with shift completion for billing automation",
      ],
    },
  ];

  const gettingStarted = [
    {
      step: 1,
      title: "Login to Your Account",
      description:
        "Use your assigned email and password to access the system. JWT tokens provide secure authentication across all microservices.",
      details: [
        "Enter your email address in the login field",
        "Type your secure password (minimum 8 characters)",
        'Click "Sign In" to receive your JWT access token',
        'If you forget your password, use the "Forgot Password" link',
        "Your session token provides access to all authorized services",
        "Token refresh happens automatically for seamless access",
      ],
    },
    {
      step: 2,
      title: "Explore Your Dashboard",
      description:
        "Your personalized dashboard aggregates real-time data from all microservices based on your role and permissions.",
      details: [
        "Review today's schedule with real-time shift status updates",
        "Check for urgent notifications from the event system",
        "View recent activity feed across all services",
        "Access quick action buttons for common workflows",
        "Monitor microservices health indicators (for managers and admins)",
        "See pending approvals for shift completions and leave requests",
      ],
    },
    {
      step: 3,
      title: "Navigate Using the Sidebar",
      description:
        "Use the navigation menu on the left to access different modules. Available options depend on your user role and permissions.",
      details: [
        "Click on module icons or names to navigate",
        "Collapsed menu shows only icons to save space",
        "Expand menu by clicking the hamburger button",
        "Module availability is based on your permission level",
        "Search functionality available for quick access",
      ],
    },
    {
      step: 4,
      title: "Update Your Profile",
      description:
        "Click on your avatar in the top-right corner to access and update your profile information and preferences.",
      details: [
        "Update contact information and emergency contacts",
        "Set your availability and working preferences",
        "Configure notification settings and preferences",
        "Upload a professional profile photo",
        "Change your password and security settings",
      ],
    },
    {
      step: 5,
      title: "Start Using Core Features",
      description:
        "Begin with basic tasks like viewing schedules, updating care notes, or managing client information based on your role.",
      details: [
        "Clock in/out for your shifts using the time tracking feature",
        "Create and update care notes for your assigned clients",
        "View and request changes to your schedule",
        "Access client information relevant to your role",
        "Submit time-off requests and availability changes",
      ],
    },
  ];

  const workflows = [
    {
      title: "Shift Management Workflow",
      icon: <Schedule />,
      steps: [
        "Manager creates shifts in the Roster module",
        "System automatically assigns based on staff availability",
        "Staff receive notifications about new assignments",
        "Staff can request changes or swap shifts",
        "Manager approves or denies change requests",
        "Confirmed schedules are published to all staff",
      ],
    },
    {
      title: "Client Onboarding Process",
      icon: <People />,
      steps: [
        "Administrator creates new client profile",
        "Care Coordinator develops initial care plan",
        "Medical information and preferences are documented",
        "Staff assignments are made based on client needs",
        "Initial care visit is scheduled and conducted",
        "Ongoing care notes and updates are maintained",
      ],
    },
    {
      title: "Care Documentation Flow",
      icon: <Note />,
      steps: [
        "Staff member accesses client's care profile",
        "Creates new care note with timestamp",
        "Documents activities, observations, and concerns",
        "Adds photos or files if necessary",
        "Submits note for supervisor review (if required)",
        "Note becomes part of permanent care record",
      ],
    },
    {
      title: "Billing and Payroll Process",
      icon: <Receipt />,
      steps: [
        "System automatically tracks staff hours worked",
        "Manager reviews and approves timesheets",
        "Client services are calculated and invoiced",
        "Payroll is processed based on approved hours",
        "Invoices are generated and sent to clients",
        "Financial reports are generated for management",
      ],
    },
  ];

  const bestPractices = [
    {
      category: "Data Security",
      icon: <Lock />,
      practices: [
        "Always log out when finished using the system",
        "Never share your login credentials with anyone",
        "Use strong passwords with mixed characters",
        "Report suspicious activity immediately",
        "Keep client information confidential at all times",
        "Only access information necessary for your role",
      ],
    },
    {
      category: "Care Documentation",
      icon: <Note />,
      practices: [
        "Document care activities immediately after completion",
        "Use clear, professional language in all notes",
        "Include specific details about client condition and responses",
        "Note any changes in client behavior or health status",
        "Record exact times for medication administration",
        "Always review notes before submitting",
      ],
    },
    {
      category: "Schedule Management",
      icon: <AccessTime />,
      practices: [
        "Check your schedule daily for any changes",
        "Submit availability updates as early as possible",
        "Communicate shift changes with team members",
        "Arrive on time and prepared for your shifts",
        "Use the system to clock in and out accurately",
        "Request time off well in advance when possible",
      ],
    },
    {
      category: "Communication",
      icon: <Phone />,
      practices: [
        "Use internal messaging system for work communications",
        "Respond to messages and notifications promptly",
        "Keep managers informed of any client concerns",
        "Participate in team meetings and training sessions",
        "Ask questions when you're unsure about procedures",
        "Maintain professional communication standards",
      ],
    },
  ];

  const troubleshooting = [
    {
      issue: "Cannot Log In",
      icon: <Lock />,
      solutions: [
        "Verify your email address is spelled correctly",
        "Check if Caps Lock is enabled when typing password",
        "Clear your browser cache and cookies",
        "Try using a different browser or incognito mode",
        "Contact your administrator for password reset",
        "Ensure your account hasn't been suspended",
      ],
    },
    {
      issue: "Page Loading Slowly",
      icon: <Speed />,
      solutions: [
        "Check your internet connection speed",
        "Close unnecessary browser tabs and applications",
        "Clear browser cache and temporary files",
        "Try accessing during off-peak hours",
        "Contact IT support if problem persists",
        "Use recommended browsers (Chrome, Firefox, Safari)",
      ],
    },
    {
      issue: "Cannot Save Changes",
      icon: <Warning />,
      solutions: [
        "Ensure all required fields are completed",
        "Check that you have permission to make changes",
        "Verify your session hasn't timed out",
        "Try refreshing the page and re-entering data",
        "Contact your supervisor if permissions are needed",
        "Save work frequently to prevent data loss",
      ],
    },
    {
      issue: "Missing Schedule Information",
      icon: <Schedule />,
      solutions: [
        "Check if schedules have been published yet",
        "Verify you're looking at the correct date range",
        "Ensure your availability has been submitted",
        "Contact your manager about schedule concerns",
        "Check for any schedule change notifications",
        "Refresh the page to see latest updates",
      ],
    },
  ];

  const securityGuidelines = [
    {
      title: "Password Security",
      icon: <VerifiedUser />,
      guidelines: [
        "Use passwords with at least 8 characters",
        "Include uppercase, lowercase, numbers, and symbols",
        "Never use personal information in passwords",
        "Change passwords every 90 days",
        "Don't reuse previous passwords",
        "Enable two-factor authentication when available",
      ],
    },
    {
      title: "Access Control",
      icon: <Visibility />,
      guidelines: [
        "Only access client information you need for your role",
        "Log out immediately when leaving your workstation",
        "Don't let others use your account",
        "Report unauthorized access attempts",
        "Keep your workstation screen locked when away",
        "Use system features instead of external tools",
      ],
    },
    {
      title: "Data Protection",
      icon: <Security />,
      guidelines: [
        "Never email client information to external addresses",
        "Don't discuss client details in public areas",
        "Secure physical documents and devices",
        "Report data breaches immediately",
        "Use encrypted communication channels",
        "Follow organizational privacy policies",
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50", pb: 10 }}>
      {/* Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        variant={isSmallScreen ? "temporary" : "persistent"}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
          // Make sure the backdrop is properly handled
          BackdropProps: {
            sx: {
              // Only show backdrop on mobile when drawer is temporary
              display: isSmallScreen ? "block" : "none",
            },
          },
        }}
        sx={{
          zIndex: isSmallScreen ? 1300 : 1200,
          "& .MuiDrawer-paper": {
            width: 300,
            boxSizing: "border-box",
            bgcolor: "background.paper",
            borderRight: "1px solid",
            borderColor: "divider",
            top: 0,
            height: "100vh",
            position: "fixed",
            zIndex: isSmallScreen ? 1300 : 1200,
            // Ensure proper overflow and scrolling
            overflowX: "hidden",
            overflowY: "auto",
          },
        }}
      >
        <NavigationDrawer />
      </Drawer>

      {/* Main Content */}
      <Container
        maxWidth="lg"
        sx={{
          ml: !isSmallScreen && drawerOpen ? "300px" : 0,
          transition: "margin-left 0.3s ease-in-out",
          width: !isSmallScreen && drawerOpen ? "calc(100% - 300px)" : "100%",
          pt: 3,
          px: isSmallScreen ? 2 : 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 2, sm: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              mb: 2,
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {(isSmallScreen || !drawerOpen) && (
              <Tooltip title="Open Navigation">
                <Button
                  variant="outlined"
                  onClick={() => {
                    console.log(
                      "Hamburger menu clicked, current state:",
                      drawerOpen,
                    );
                    setDrawerOpen(!drawerOpen);
                  }}
                  sx={{
                    mr: { xs: 1, sm: 2 },
                    minWidth: "auto",
                    p: { xs: 1, sm: 1.5 },
                    cursor: "pointer",
                    userSelect: "none",
                    touchAction: "manipulation",
                    flexShrink: 0,
                  }}
                >
                  <MenuIcon />
                </Button>
              </Tooltip>
            )}
            <Typography
              variant="h3"
              component="h1"
              color="primary"
              fontWeight={600}
              sx={{
                fontSize: {
                  xs: "1.75rem", // 28px on extra small screens
                  sm: "2rem", // 32px on small screens
                  md: "2.5rem", // 40px on medium screens
                  lg: "3rem", // 48px on large screens (h3 default)
                },
                lineHeight: {
                  xs: 1.2,
                  sm: 1.3,
                  md: 1.4,
                },
                textAlign: { xs: "center", sm: "left" },
                wordBreak: "break-word",
                hyphens: "auto",
              }}
            >
              Felice Care Management System
            </Typography>
          </Box>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              mb: 3,
              fontSize: {
                xs: "0.9rem", // 14.4px on extra small screens
                sm: "1rem", // 16px on small screens
                md: "1.1rem", // 17.6px on medium screens
                lg: "1.25rem", // 20px on large screens (h6 default)
              },
              textAlign: { xs: "center", sm: "left" },
              wordBreak: "break-word",
            }}
          >
            Comprehensive Documentation and User Guide
          </Typography>
          <Divider />
        </Box>

        {/* System Overview */}
        <Card id="overview" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              System Overview
            </Typography>
            <Typography variant="body1" paragraph>
              The Felice Care Management System is a modern, microservices-based
              platform designed to streamline care operations, staff management,
              and client services across healthcare and home care organizations.
              Built with .NET Core microservices architecture and React
              frontend, it provides role-based access to ensure users can access
              the features they need while maintaining strict security and data
              integrity protocols.
            </Typography>
            <Typography variant="body1" paragraph>
              Our system consists of multiple specialized services including
              Authentication, Staff Management, Client Management, Roster
              Scheduling, and Billing services, all coordinated through an API
              Gateway. The platform includes automated workflows, real-time
              event-driven communication, and comprehensive reporting
              capabilities powered by Azure SQL Server and RabbitMQ messaging.
            </Typography>
            <Typography variant="body1" paragraph>
              Key system capabilities include advanced shift completion
              workflows with dual approval systems, comprehensive care
              documentation with audit trails, automated billing and invoice
              generation, mobile-responsive design for field access, and
              enterprise-grade security with JWT authentication and encrypted
              data transmission.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                System Highlights
              </Typography>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                sx={{ flexWrap: "wrap" }}
              >
                <Chip
                  icon={<Security />}
                  label="Microservices Architecture"
                  color="primary"
                />
                <Chip
                  icon={<Speed />}
                  label="Event-Driven Communication"
                  color="info"
                />
                <Chip
                  icon={<Timeline />}
                  label="Advanced Shift Workflows"
                  color="warning"
                />
                <Chip
                  icon={<Receipt />}
                  label="Automated Billing"
                  color="success"
                />
                <Chip
                  icon={<Lock />}
                  label="JWT Authentication"
                  color="secondary"
                />
              </Stack>
            </Box>
          </CardContent>
        </Card>

        {/* Getting Started */}
        <Card id="getting-started" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Getting Started
            </Typography>
            <Stack spacing={2}>
              {gettingStarted.map((item, index) => (
                <Box key={index}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                        fontWeight: "bold",
                      }}
                    >
                      {item.step}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        {item.description}
                      </Typography>
                      <Accordion sx={{ mt: 1 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" fontWeight={600}>
                            Detailed Steps
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {item.details.map((detail, dIndex) => (
                              <ListItem key={dIndex} sx={{ py: 0.25 }}>
                                <ListItemIcon sx={{ minWidth: 24 }}>
                                  <CheckCircle
                                    color="success"
                                    sx={{ fontSize: 16 }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={detail}
                                  primaryTypographyProps={{ variant: "body2" }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* User Roles */}
        <Card id="user-roles" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              User Roles & Permissions
            </Typography>
            <Typography variant="body1" paragraph>
              The system uses a role-based access control system with four main
              user levels:
            </Typography>
            <Stack spacing={3}>
              {userRoles.map((role, index) => (
                <Box key={index}>
                  <Card variant="outlined" sx={{ height: "100%" }}>
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Chip
                          label={`Level ${role.level}`}
                          color={role.color as any}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="h6">{role.name}</Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        {role.description}
                      </Typography>
                      <Typography variant="subtitle2" gutterBottom>
                        Key Permissions:
                      </Typography>
                      <List dense>
                        {role.permissions
                          .slice(0, 4)
                          .map((permission, pIndex) => (
                            <ListItem key={pIndex} sx={{ py: 0.25 }}>
                              <ListItemIcon sx={{ minWidth: 20 }}>
                                <Security
                                  color="primary"
                                  sx={{ fontSize: 16 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={permission}
                                primaryTypographyProps={{ variant: "body2" }}
                              />
                            </ListItem>
                          ))}
                      </List>
                    </CardContent>
                  </Card>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* System Modules */}
        <Card id="system-modules" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              System Modules
            </Typography>
            <Typography variant="body1" paragraph>
              The system is organized into several key modules, each designed to
              handle specific aspects of care management operations:
            </Typography>

            {modules.map((module, index) => (
              <Accordion key={index} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Box sx={{ mr: 2, color: "primary.main" }}>
                      {module.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6">{module.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {module.description}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="subtitle2" gutterBottom>
                    Key Features:
                  </Typography>
                  <List>
                    {module.features.map((feature, fIndex) => (
                      <ListItem key={fIndex}>
                        <ListItemIcon>
                          <Assignment color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>

        {/* Common Workflows */}
        <Card id="workflows" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Common Workflows
            </Typography>
            <Typography variant="body1" paragraph>
              Understanding standard workflows helps ensure consistent
              operations and quality care delivery. Here are the key processes
              used throughout the system:
            </Typography>

            <Stack spacing={3}>
              {workflows.map((workflow, index) => (
                <Card key={index} variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box sx={{ mr: 2, color: "primary.main" }}>
                      {workflow.icon}
                    </Box>
                    <Typography variant="h6" color="primary">
                      {workflow.title}
                    </Typography>
                  </Box>
                  <Box sx={{ pl: 5 }}>
                    {workflow.steps.map((step, stepIndex) => (
                      <Box
                        key={stepIndex}
                        sx={{ display: "flex", alignItems: "center", mb: 1 }}
                      >
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            bgcolor: "secondary.main",
                            color: "white",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                            fontSize: "0.75rem",
                            fontWeight: "bold",
                          }}
                        >
                          {stepIndex + 1}
                        </Box>
                        <Typography variant="body2">{step}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Best Practices */}
        <Card id="best-practices" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Best Practices & Guidelines
            </Typography>
            <Typography variant="body1" paragraph>
              Following these best practices ensures optimal system performance,
              data security, and quality care delivery. These guidelines are
              based on industry standards and organizational policies.
            </Typography>

            <Stack spacing={3}>
              {bestPractices.map((category, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ mr: 2, color: "primary.main" }}>
                        {category.icon}
                      </Box>
                      <Typography variant="h6">{category.category}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List>
                      {category.practices.map((practice, pIndex) => (
                        <ListItem key={pIndex}>
                          <ListItemIcon>
                            <CheckCircle color="success" />
                          </ListItemIcon>
                          <ListItemText primary={practice} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Security Guidelines */}
        <Card id="security-guidelines" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Security Guidelines
            </Typography>
            <Typography variant="body1" paragraph>
              Data security and client privacy are paramount in healthcare
              operations. All users must follow these security guidelines to
              maintain compliance with HIPAA, organizational policies, and
              industry best practices.
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: "error.light", borderRadius: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Warning color="error" sx={{ mr: 1 }} />
                <Typography variant="h6" color="error.main">
                  Critical Security Notice
                </Typography>
              </Box>
              <Typography variant="body2" color="error.contrastText">
                Violation of security policies may result in immediate account
                suspension, disciplinary action, and potential legal
                consequences. When in doubt, contact your supervisor or IT
                security team.
              </Typography>
            </Box>

            <Stack spacing={3}>
              {securityGuidelines.map((guideline, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{ border: "1px solid", borderColor: "warning.main" }}
                >
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box sx={{ mr: 2, color: "warning.main" }}>
                        {guideline.icon}
                      </Box>
                      <Typography variant="h6" color="warning.main">
                        {guideline.title}
                      </Typography>
                    </Box>
                    <List dense>
                      {guideline.guidelines.map((item, gIndex) => (
                        <ListItem key={gIndex} sx={{ py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <Security color="warning" sx={{ fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={item}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card id="troubleshooting" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Troubleshooting Guide
            </Typography>
            <Typography variant="body1" paragraph>
              Common issues and their solutions. Try these steps before
              contacting technical support. If problems persist after following
              these solutions, please document the error messages and contact
              your IT support team.
            </Typography>

            <Stack spacing={3}>
              {troubleshooting.map((problem, index) => (
                <Accordion key={index}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Box sx={{ mr: 2, color: "error.main" }}>
                        {problem.icon}
                      </Box>
                      <Typography variant="h6">{problem.issue}</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      color="primary"
                    >
                      Try these solutions in order:
                    </Typography>
                    <List>
                      {problem.solutions.map((solution, sIndex) => (
                        <ListItem key={sIndex}>
                          <ListItemIcon>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                bgcolor: "info.main",
                                color: "white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.7rem",
                                fontWeight: "bold",
                              }}
                            >
                              {sIndex + 1}
                            </Box>
                          </ListItemIcon>
                          <ListItemText primary={solution} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          </CardContent>
        </Card>

        {/* Advanced Features */}
        <Card id="advanced-features" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Advanced Features
            </Typography>
            <Typography variant="body1" paragraph>
              These advanced features are available to users with appropriate
              permissions and training. Contact your administrator if you need
              access to any of these capabilities.
            </Typography>

            <Stack spacing={3}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Timeline color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6">
                      Advanced Analytics & Reporting
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Real-time analytics powered by microservices data
                    aggregation. Generate comprehensive reports on staff
                    performance, client outcomes, financial metrics, shift
                    completion rates, and operational efficiency. Export data in
                    multiple formats with automated report scheduling.
                  </Typography>
                  <Chip label="Manager+ Access" color="warning" size="small" />
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Settings color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6">
                      Microservices Configuration
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Advanced system configuration including microservices
                    settings, API Gateway routing, event-driven workflow
                    configurations, JWT authentication parameters, and database
                    connection management across all services.
                  </Typography>
                  <Chip label="Admin Only" color="error" size="small" />
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <FileUpload color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6">
                      Data Integration & Migration
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Bulk import/export capabilities across all microservices.
                    Import client data, staff information, and schedules from
                    external systems. Advanced data migration tools for system
                    upgrades and backup operations with data integrity
                    validation.
                  </Typography>
                  <Chip label="Admin + Manager" color="warning" size="small" />
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <NotificationImportant color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6">
                      Event-Driven Notifications
                    </Typography>
                  </Box>
                  <Typography variant="body2" paragraph>
                    Real-time automated notifications powered by RabbitMQ event
                    system. Configure alerts for shift completions, billing
                    events, medication reminders, appointment alerts, compliance
                    deadlines, and emergency situations with cross-service
                    coordination.
                  </Typography>
                  <Chip label="Care Coordinator+" color="info" size="small" />
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>

        {/* Training Resources */}
        <Card id="training-resources" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Training & Learning Resources
            </Typography>
            <Typography variant="body1" paragraph>
              Continuous learning ensures effective system use and quality care
              delivery. These resources help users stay current with system
              features and best practices.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
              <Box sx={{ flex: 1 }}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <School color="primary" sx={{ fontSize: 48 }} />
                      <Typography variant="h6" gutterBottom>
                        New User Training
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="System overview and navigation" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Role-specific feature training" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Security and compliance requirements" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Hands-on practice sessions" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <Build color="primary" sx={{ fontSize: 48 }} />
                      <Typography variant="h6" gutterBottom>
                        Advanced Training
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Advanced reporting and analytics" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="System administration functions" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Custom workflow configuration" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Integration with external systems" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Card variant="outlined" sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ textAlign: "center", mb: 2 }}>
                      <Help color="primary" sx={{ fontSize: 48 }} />
                      <Typography variant="h6" gutterBottom>
                        Ongoing Support
                      </Typography>
                    </Box>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Monthly system updates training" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Q&A sessions with IT support" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="User feedback and improvement sessions" />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Refresher training as needed" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Support Information */}
        <Card id="support" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Support & Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              Our support team is available to help with technical issues,
              training needs, and system questions. Please use the appropriate
              contact method based on your specific needs and urgency level.
            </Typography>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={3}
              sx={{ mb: 4 }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ textAlign: "center" }}>
                  <AccountCircle color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    System Administrator
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For account creation, role changes, microservices
                    configuration, and system administration
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    Response Time: Within 2 hours
                  </Typography>
                  <Typography variant="body2">
                    Email: admin@felicecare.com.au
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Phone color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    Technical Support
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For technical issues, microservices problems, database
                    connectivity, and system bugs
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    Response Time: Within 1 hour
                  </Typography>
                  <Typography variant="body2">Phone: 0416615147</Typography>
                  <Typography variant="body2">
                    Email: admin@felicecare.com.au
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ textAlign: "center" }}>
                  <Email color="primary" sx={{ fontSize: 48, mb: 1 }} />
                  <Typography variant="h6" gutterBottom>
                    User Training
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    For training sessions and system usage guidance
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    Response Time: Within 24 hours
                  </Typography>
                  <Typography variant="body2">
                    Email: admin@felicecare.com.au
                  </Typography>
                </Box>
              </Box>
            </Stack>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom color="primary">
                Emergency Support
              </Typography>
              <Box sx={{ p: 2, bgcolor: "error.light", borderRadius: 1 }}>
                <Typography variant="body2" color="error.contrastText">
                  <strong>24/7 Hotline: 0416615147</strong>
                  <br />
                  For critical system failures affecting client care or safety.
                  Only use for genuine emergencies that cannot wait for regular
                  support hours.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{ mt: 3, p: 2, bgcolor: "primary.light", borderRadius: 1 }}
            >
              <Typography variant="body2" color="primary.contrastText">
                <strong>Important:</strong> Always contact your immediate
                supervisor or system administrator before making any changes to
                client information or care plans. Ensure you understand your
                role permissions and responsibilities within the system.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Frequently Asked Questions */}
        <Card id="faq" sx={{ mb: 4 }}>
          <CardContent>
            <Typography
              variant="h4"
              gutterBottom
              color="primary"
              sx={responsiveH4Style}
            >
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" paragraph>
              Common questions and answers to help you quickly resolve issues
              and understand system functionality.
            </Typography>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    How do I reset my password?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Click the "Forgot Password" link on the login page, enter your
                  email address, and follow the instructions sent to your email.
                  If you don't receive the email within 10 minutes, check your
                  spam folder or contact your administrator.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    Why can't I see certain menu options?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Menu visibility is based on your user role and permissions. If
                  you need access to additional features, contact your manager
                  or administrator to request appropriate permissions.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    How do I request time off?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Go to the Roster module, click on "Time Off Requests," select
                  your dates, provide a reason, and submit. Your manager will
                  receive a notification and respond within 48 hours.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    Can I access the system on my mobile device?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Yes, the system is fully responsive and works on smartphones
                  and tablets. Use your regular login credentials in any web
                  browser. For best experience, use Chrome, Safari, or Firefox
                  mobile browsers.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    How do I clock in and out for shifts?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Navigate to your Dashboard and click the "Clock In" button
                  when you start your shift. The system will track your location
                  if enabled. Click "Clock Out" when your shift ends. Late
                  clock-ins or early clock-outs may require manager approval.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    What browsers are supported?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  The system works best with modern browsers: Chrome
                  (recommended), Firefox, Safari, and Edge. Internet Explorer is
                  not supported. Keep your browser updated for optimal
                  performance and security.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    How long do care notes remain in the system?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Care notes are retained according to legal and organizational
                  requirements, typically 7-10 years depending on local
                  regulations. Archived notes remain accessible but may require
                  special permissions to view.
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <QuestionMark color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6">
                    Can I edit care notes after submitting them?
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" paragraph>
                  Once submitted, care notes cannot be deleted but can be
                  amended with additional notes marked as updates. All changes
                  are tracked with timestamps and user identification for audit
                  purposes. Contact your supervisor if corrections are needed.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </CardContent>
        </Card>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" color="primary" gutterBottom>
            Felice Care Management System
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Version 2.0.0 - Microservices Architecture | Last Updated: September
            19, 2025
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" color="text.secondary">
              © 2025 Felice Care Services
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visit our website:{" "}
              <a
                href="https://felicecare.com.au/"
                target="_blank"
                rel="noopener"
                style={{ color: "inherit", fontWeight: 500 }}
              >
                felicecare.com.au
              </a>
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            This documentation is proprietary and confidential. Unauthorized
            distribution is prohibited.
          </Typography>
        </Box>
      </Container>

      {/* Floating Action Buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        <Tooltip title="Table of Contents" placement="left">
          <Fab
            color="primary"
            onClick={() => setDrawerOpen(!drawerOpen)}
            sx={{
              boxShadow: 3,
              "&:hover": {
                transform: "scale(1.1)",
                transition: "transform 0.2s ease-in-out",
              },
            }}
          >
            <Toc />
          </Fab>
        </Tooltip>

        <Tooltip title="Back to Top" placement="left">
          <Fab
            color="secondary"
            size="medium"
            onClick={scrollToTop}
            sx={{
              boxShadow: 3,
              "&:hover": {
                transform: "scale(1.1)",
                transition: "transform 0.2s ease-in-out",
              },
            }}
          >
            <KeyboardArrowUp />
          </Fab>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default DocumentationPage;
